import { ContactUsType, LoyaltyChallengeConfig } from '@shared/models';

import {
  BARCODE_FORMAT,
  DeviceType,
  DISPLAY_CONDITION,
  OfferType,
  ProductCheckTabType,
  RewardFormat,
  ScanCheckMode,
  ScorpioSubType,
  SubType,
  TitleType,
  Variants,
} from '../utils';

export const SCORPIO_CONFIG: LoyaltyChallengeConfig = {
  currency: 'USD',
  appIcon: 'assets/images/scorpio/favicon.ico',
  cssVariables: {
    'font-family-regular': 'Lato-Regular',
    'font-family-bold': 'Lato-Bold',

    'navbar-height': '65px',
    'navbar-background-color': '#ffffff',

    'nav-link-color': '#333333',
    'nav-link-hover-color': '#272F35',
    'nav-link-active-color': '#0347A8',

    'nav-item-bg-color': 'white',
    'nav-item-active-bg-color': 'white',
    'nav-item-font-family': 'var(--font-family)',
    'nav-item-hover-bg-color': 'rgb(0 0 0 / 10%)',

    'body-color': '#000000',
    'body-background-color': '#ffffff',

    'challenge-primary-color': '#0347A8',

    'tooltip-text-color': 'white',
    'tooltip-background-color': 'black',
  },
  rewardFormat: RewardFormat.POINTS,
  rewardTemplate: '{points}<span style="font-size: 14px;"></span>',
  og: {
    title: 'Current Challenges | FairPrice',
    description:
      'Discover current challenges at FairPrice and earn rewards on your favorite products.',
  },
  pages: {
    availablePages: ['help', 'current', 'history'],
    help: {
      meta: {
        title: 'Current Challenges | Loyalty Challenge | Help Center',
        keywords:
          'loyalty, challenge, current, challenges, loyalty challenge, loyalty challenge current challenges',
        description:
          "Discover the current challenges and earn up to $30 by buying your favorite brands. Don't miss out on the opportunity to win big!",
      },
      header: {
        background: '#0347A8',
        bannerAlternativeText: '',
        goBackButton: {
          text: 'Go Back To My Challenges',
          backgroundColor: '#F1F6FE',
          color: '#000000',
          fontFamily: 'var(--font-family-bold)',
          borderRadius: '5px',
        },
        howToPlay: {
          specificDisplayCondition: DISPLAY_CONDITION.ALWAYS,
          title: {
            color: 'white',
            text: 'How it works?',
            fontFamily: 'var(--font-family-bold)',
          },
        },
      },
      faqVariant: {
        variant: Variants.VARIANT_1,
        title: {
          displayWhenCampaignBis: false,
          type: TitleType.TEXT,
          label: 'Questions',
          styles: {
            color: '#0347A8',
            fontSize: '28px',
            fontFamily: 'var(--font-family-bold)',
          },
        },
        questions: [
          {
            title: 'How do I find and join a challenge?',
            content:
              '<p style="margin: 0 0 0.5rem;">To get started, follow these simple steps:</p><ul style="margin: 0 0 0.5rem; padding-left: 1.25rem;"><li>Open your <span style="font-family: var(--font-family-bold)">FairPrice Group app</span> and tap the <span style="font-family: var(--font-family-bold)">Members Exclusive Challenge</span> banner on the home screen.</li><li>Browse the available challenges and tap <span style="font-family: var(--font-family-bold)">“Start Challenge”</span> on the ones you want to join.</li></ul><p style="margin: 0 0 0.5rem;"><span style="font-family: var(--font-family-bold)">Key things to note:</span></p><ul style="margin: 0; padding-left: 1.25rem;"><li><span style="font-family: var(--font-family-bold)">Limit:</span> You can join up to 10 challenges each month.</li><li><span style="font-family: var(--font-family-bold)">Personalised:</span> Everyone gets a unique set of challenges tailored just for them.</li><li><span style="font-family: var(--font-family-bold)">Cancellations:</span> If you have not made a qualifying purchase toward a challenge yet, you can cancel it and choose a different one.</li><li><span style="font-family: var(--font-family-bold)">Important:</span> Once you make a purchase and progress on a challenge, it is locked in and cannot be cancelled or swapped.</li></ul>',
          },
          {
            title: 'How long does the Challenge run for?',
            content:
              '<p style="margin: 0 0 0.5rem;">The overall campaign runs across two monthly cycles: 1 to 31 August 2026 and 1 to 30 September 2026.</p><p style="margin: 0;">Individual challenges and eligibility refresh on a monthly basis. You can check your available challenges and their specific validity periods directly on the Members Exclusive Challenge page in your FairPrice Group app each month.</p>',
          },
          {
            title: 'How does the Spend challenge work?',
            content:
              '<p style="margin: 0 0 0.5rem;">Each Spend challenge features 3 cumulative spend milestones. Both spending progress and displayed Linkpoints rewards are cumulative, with each reached milestone unlocking an additional tier of Linkpoints. Your exact spending targets and rewards will be clearly displayed on the challenge page.</p><p style="margin: 0;">Note: Linkpoints are not credited instantly upon hitting a target. Earned Linkpoints will be credited every Tuesday by 6:00 PM for all spend milestones and Frequency Challenges completed in the preceding week.</p>',
          },
          {
            title: 'How does the Frequency challenge work?',
            content:
              '<p style="margin: 0 0 0.5rem;">Instead of tracking a total dollar amount, Frequency challenges require you to complete a specific number of Qualifying Visits at participating brands.</p><p style="margin: 0 0 0.5rem;"><span style="font-family: var(--font-family-bold)">Key things to note:</span></p><ul style="margin: 0; padding-left: 1.25rem;"><li><span style="font-family: var(--font-family-bold)">Daily Limit:</span> You can earn a maximum of 1 Qualifying Visit per day for each challenge by meeting its required minimum spend. You can combine multiple receipts within the same day to hit the minimum spend.</li><li><span style="font-family: var(--font-family-bold)">Trust:</span> Payment must be made via the FairPrice Group app using a saved Trust Link or NTUC Link Credit/Debit Card at FairPrice (in-store/online), Kopitiam and Unity. Apple Pay and Google Pay transactions are not eligible.</li></ul>',
          },
          {
            title:
              'How do I track my progress, and how long does it take to update?',
            content:
              '<p style="margin: 0 0 0.5rem;">To see your progress, tap the Members Exclusive Challenge banner on the home screen of your FairPrice Group app.</p><p style="margin: 0;">It can take up to 24 hours from your in-store transaction or after your online order is successfully delivered for your progress bar to update in the app.</p>',
          },
          {
            title:
              'If I made a purchase before accepting a challenge, will it still count toward my milestones?',
            content:
              '<p style="margin: 0;">No, your spending and visits will only start counting after you have tapped “Start Challenge” in the app. Any purchases made before you join the challenge will not be factored, so make sure to accept your challenges before you head to the checkout.</p>',
          },
          {
            title:
              'How do I know how many Linkpoints I can get for each Spend challenge milestone or Frequency challenge?',
            content:
              '<p style="margin: 0 0 0.5rem;">You can view your specific targets and reward directly on the Challenge page:</p><ul style="margin: 0 0 0.5rem; padding-left: 1.25rem;"><li><span style="font-family: var(--font-family-bold)">For Spend challenge:</span> Each challenge features 3 cumulative spend milestones. Both spending progress and displayed Linkpoints rewards are cumulative, with each reached milestone unlocking an additional tier of Linkpoints.</li><li><span style="font-family: var(--font-family-bold)">For Frequency challenge:</span> Each challenge will display the total number of Qualifying Visits required and the fixed amount of Linkpoints you will receive upon successful completion of that target.</li></ul><p style="margin: 0 0 0.5rem;"><span style="font-family: var(--font-family-bold)">Example of how Spend challenge milestones work:</span></p><ul style="margin: 0 0 0.5rem; padding-left: 1.25rem;"><li><span style="font-family: var(--font-family-bold)">Milestone 1 ($5 spend):</span> Unlocks 110 Linkpoints → You receive 110 Linkpoints</li><li><span style="font-family: var(--font-family-bold)">Milestone 2 ($10 spend):</span> Cumulative reward is 220 Linkpoints → You receive an additional 110 Linkpoints</li><li><span style="font-family: var(--font-family-bold)">Milestone 3 ($15 spend):</span> Cumulative reward is 350 Linkpoints → You receive an additional 130 Linkpoints</li></ul><p style="margin: 0;">The maximum total amount of Linkpoints you can earn from completing all 3 milestones in this Spend challenge example is 350 Linkpoints.</p>',
          },
          {
            title: 'When will my earned Linkpoints be credited to my account?',
            content:
              '<p style="margin: 0;">Earned Linkpoints will be credited every Tuesday by 6:00 PM for all spend milestones and Frequency Challenges completed in the preceding week. You can find the credited amount under ‘Members Exclusive Challenge’ in your Linkpoints transaction history on the FairPrice Group app.</p>',
          },
          {
            title: 'How do I achieve my Qualifying Spend/Visits?',
            content:
              '<p style="margin: 0 0 0.5rem;">To ensure your purchases are tracked, your FairPrice Group app account must be linked to your Link Rewards card before you check out.</p><p style="margin: 0 0 0.25rem;"><span style="font-family: var(--font-family-bold)">1. In-Store</span></p><p style="margin: 0 0 0.5rem;">Present or use one of the following methods at checkout:</p><ul style="margin: 0 0 0.5rem; padding-left: 1.25rem;"><li>FairPrice Group app: Use ‘Pay / Earn’ or Scan &amp; Go</li><li>Physical Card: Tap or present your physical Link Rewards card</li></ul><p style="margin: 0 0 0.75rem;">Note: For Kopitiam transactions, payments must be made via the FairPrice Group app.</p><p style="margin: 0 0 0.25rem;"><span style="font-family: var(--font-family-bold)">2. Online</span></p><p style="margin: 0 0 0.5rem;">Ensure you are logged into your linked FairPrice Group account when placing your order on the FairPrice website or app.</p><p style="margin: 0 0 0.75rem;">Note: Online orders must be both placed and delivered within the same specific monthly Challenge Period to count toward that month’s challenge.</p><p style="margin: 0 0 0.25rem;"><span style="font-family: var(--font-family-bold)">3. Pay with Trust Frequency challenge</span></p><p style="margin: 0 0 0.5rem;">Payment must be made via the FairPrice Group app using a saved Trust Link or NTUC Link Credit/Debit Card at FairPrice (in-store/online), Kopitiam and Unity.</p><p style="margin: 0;">Note: Physical cards as well as Apple Pay or Google Pay will not qualify for this challenge.</p>',
          },
          {
            title: 'Are there any items excluded from the Qualifying Spend?',
            content:
              '<p style="margin: 0 0 0.5rem;">Yes. The following items do not count toward your milestones:</p><ul style="margin: 0; padding-left: 1.25rem;"><li>Statutory items (Singapore Pools products: 4D, TOTO, Singapore Sweep)</li><li>FairPrice Group Gift Vouchers / Gift Cards</li><li>Tobacco products</li><li>Infant milk powder (0–12 months)</li><li>Prescription and pharmacy-only medicines</li><li>Non-merchandise items (e.g., carrier bag charges, membership/consultation fees)</li><li>Delivery and service fees for online orders</li></ul>',
          },
          {
            title:
              'I accidentally started a challenge. Can I cancel or swap it?',
            content:
              '<p style="margin: 0 0 0.5rem;">It depends on whether you have started shopping yet:</p><ul style="margin: 0; padding-left: 1.25rem;"><li><span style="font-family: var(--font-family-bold)">Before making a purchase:</span> If your progress bar is still at 0% and you have not made any qualifying transactions toward that challenge, you can cancel it and select a different one.</li><li><span style="font-family: var(--font-family-bold)">After making a purchase:</span> Once you make a qualifying purchase and progress is logged, the challenge is locked in and cannot be cancelled, removed, or swapped for the rest of the month.</li></ul>',
          },
          {
            title:
              'What does it mean if a challenge says “Challenge fully redeemed”?',
            content:
              '<p style="margin: 0;">Our challenges have an overall maximum redemption cap. If a specific challenge hits its limit, it will display a “Challenge fully redeemed” message and close to new participants. However, if you already accepted and started the challenge before it hit the cap, you will still be allowed to complete it and earn your rewards within the validity period.</p>',
          },
          {
            title:
              'Why can’t my family or friends see the Members Exclusive Challenge?',
            content:
              '<p style="margin: 0;">The Members Exclusive Challenge is a personalised campaign rolled out to selected accounts during this period. As these challenges are tailored individually, availability may vary from member to member.</p>',
          },
        ],
        accordion: {
          styles: {
            title: {
              fontFamily: 'var(--font-family-bold)',
              fontSize: '15px',
              color: '#333333',
            },
            description: {
              fontSize: '14px',
              lineHeight: '1.6',
              color: '#333333',
              fontFamily: 'var(--font-family-regular)',
            },
          },
          alternativeTextExpandMore: 'Expand answer',
          alternativeTextExpandLess: 'Collapse answer',
        },
      },
      contactUs: {
        type: ContactUsType.TEMPLATE,
        config: [
          '<div style="margin: 1rem;color: #333333;">',
          '<p style="font-family: var(--font-family-bold); margin-bottom: 15px;">Who can I contact if I need further assistance?</p>',
          '<p style="font-family: var(--font-family-regular); margin-bottom: 0px;font-size: 15px;">Contact our customer service hotline at <span style="color: #1454B8;">6380 5858</span> </br> or</p>',
          '<p style="font-family: var(--font-family-regular);font-size: 15px;">submit your query <a style="color: #1454B8; font-family:var(--font-family-bold)" target="_blank" href= "https://help.fairprice.com.sg/hc/en-us/requests/new" >here</a></p>',
          '</div>',
        ],
      },
      templateBeforeContactForm: [
        '<div style="padding: 2rem 0; margin: 0 auto; width: 85%; max-width: 860px; color: #333333; box-sizing: border-box;">',
        '<h1 style="font-size: 28px; font-family: var(--font-family-bold); color: var(--challenge-primary-color); text-align: center; margin: 0 0 0.25rem;">Terms and Conditions</h1>',
        '<p style="font-size: 16px; font-family: var(--font-family-bold); text-align: center; margin: 0 0 1.5rem;">FairPrice Group app Members Exclusive Challenge</p>',
        '<ol style="font-size: 14px; font-family: var(--font-family-regular); line-height: 1.6; padding-left: 1.5rem; margin: 0;">',
        '<li style="margin-bottom: 0.75rem;">The FairPrice Group app Members Exclusive Challenge (“<span style="font-family: var(--font-family-bold)">Challenge</span>”) is organised by NTUC FairPrice Co-operative Ltd and NTUC Link Pte Ltd (collectively, the “Organiser”).</li>',
        '<li style="margin-bottom: 0.75rem;">Participation in this Challenge is subject to these Terms and Conditions. Entry into this Challenge is deemed acceptance of these Terms and Conditions and any amendments, additions, replacements, variations and modifications hereto shall be final and binding in all respects on each participant.</li>',
        '<li style="margin-bottom: 0.75rem;">The Challenge is a <span style="font-family: var(--font-family-bold)">monthly recurring event</span>. The specific validity period for each monthly Challenge will be stated on the Members Exclusive Challenge page. The overall Challenge is valid from {variable} at 00:00 to {variable} at 23:59 (“<span style="font-family: var(--font-family-bold)">Challenge Period</span>”).</li>',
        '<li style="margin-bottom: 0.75rem;">The Challenge is available to selected targeted customers only on a monthly basis who: (i) registered users on the FairPrice Group app with a valid email address; (ii) have a valid Link account connected to the FairPrice Group app; and (iii) have been notified of their eligibility via the FairPrice Group app, push notification or email.',
        '<ol type="a" style="margin: 0.5rem 0 0; padding-left: 1.25rem;">',
        '<li style="margin-bottom: 0.5rem;">Challenges cannot be shared. The Organiser reserves the right to limit the accessibility of participants to the Challenge in their app.</li>',
        '<li style="margin-bottom: 0.5rem;">Challenge is available only on the FairPrice Group app, downloaded from the Apple App Store (iOS version 7.27.0 and after) &amp; Google Play Store (Android version 7.24.0 and after).</li>',
        '<li style="margin-bottom: 0.5rem;">To view the Challenge on the FairPrice Group app, open the app and tap on the Members Exclusive Challenge banner.<img src="assets/images/scorpio/tc-members-exclusive-challenge.webp" alt="Screenshot of the FairPrice Group app home screen with the Members Exclusive Challenge banner highlighted" style="display: block; width: 160px; max-width: 100%; height: auto; margin: 0.5rem auto; border: 1px solid #e0e0e0; border-radius: 8px;" /></li>',
        '</ol>',
        '</li>',
        '<li style="margin-bottom: 0.75rem;">Customers participate in the Challenge by starting at least one of the challenges on the FairPrice Group app (“<span style="font-family: var(--font-family-bold)">Participants</span>”). Participants can select up to 10 challenges to participate in each month.</li>',
        '<li style="margin-bottom: 0.75rem;">Each Participant can earn Linkpoints upon successful completion of the Challenge, in accordance with the criteria and requirements applicable to each Challenge (if any). The Linkpoints earned will vary according to the number of Challenges completed.',
        '<ol type="a" style="margin: 0.5rem 0 0; padding-left: 1.25rem;">',
        '<li style="margin-bottom: 0.5rem;">Challenges may feature either spend milestones or visit targets: Spend challenges: Feature 3 cumulative spend milestones. Both spending progress and displayed Linkpoints rewards are cumulative, with each reached milestone unlocking an additional tier of Linkpoints. Frequency challenges: Require a specific number of visits, with Linkpoints awarded upon successfully completing the target.</li>',
        '<li style="margin-bottom: 0.5rem;">The exact requirements (spend amounts or number of visits) and corresponding Linkpoints rewards will be clearly displayed on the Participant’s FairPrice Group app.</li>',
        '<li style="margin-bottom: 0.5rem;">Progress of the Challenge may take up to 24 hours after the date of in-store transaction or date of delivery to update in the FairPrice Group app.</li>',
        '</ol>',
        '</li>',
        '<li style="margin-bottom: 0.75rem;">Linkpoints are not credited instantly upon hitting a target. Earned Linkpoints will be credited every Tuesday by 6:00 PM for all spend milestones and Frequency challenges completed in the preceding week.</li>',
        '<li style="margin-bottom: 0.75rem;">In the event any of the Challenge(s) have reached the redemption cap, the Challenge will display “Challenge fully redeemed” messaging. Participants who joined the challenge before it reached the redemption cap can still complete it and earn their rewards.</li>',
        '<li style="margin-bottom: 0.75rem;">Spend that is valid for this Challenge (“<span style="font-family: var(--font-family-bold)">Qualifying Spend</span>”) includes all transactions associated with the Link Rewards card registered under the account holder, made in-store at any FairPrice, FairPrice Finest, FairPrice Xtra, Unity store, Kopitiam outlet, or online at <a href="https://www.fairprice.com.sg/" target="_blank" rel="noopener noreferrer" style="color: #0347A8;">https://www.fairprice.com.sg/</a> (“<span style="font-family: var(--font-family-bold)">Participating Stores</span>”).',
        '<ol type="a" style="margin: 0.5rem 0 0; padding-left: 1.25rem;">',
        '<li style="margin-bottom: 0.5rem;">In-store transactions must be made with the Link Rewards card or with the FairPrice Group app through Pay via FairPrice Group app or Scan and Go. The FairPrice Group app account must be linked to the Link Rewards card registered under the sole recipient before checkout.</li>',
        '<li style="margin-bottom: 0.5rem;">Online orders made at FairPrice (www.fairprice.com.sg) or via the FairPrice Group app must be placed and successfully delivered within the specific monthly Challenge Period to qualify.</li>',
        '<li style="margin-bottom: 0.5rem;">Transactions at Kopitiam must be paid for via the FairPrice Group app.</li>',
        '<li style="margin-bottom: 0.5rem;">Qualifying Spend will exclude all discounts, promotions, offers, Linkpoints exclusion items, delivery fees and services fees, unless otherwise stated.</li>',
        '<li style="margin-bottom: 0.5rem;">Purchase of the following items will not count towards the Challenge: statutory items (Singapore Pools betting products — 4D, Singapore Sweep and TOTO), FairPrice Group Gift Vouchers, including top-ups and service fees, non-merchandise (carrier bags, membership and consultation fees), tobacco products, infant milk powder (0-12 months), prescription and pharmacy-only medicines, as well as the delivery and service fees for online purchases made at FairPrice (www.fairprice.com.sg).</li>',
        '<li style="margin-bottom: 0.5rem;">Challenge is only valid for orders for personal consumption. Business-to-Business (B2B) Orders will not count towards the spending attributed to the challenges, unless otherwise stated.</li>',
        '</ol>',
        '</li>',
        '<li style="margin-bottom: 0.75rem;">Visits that qualify for Frequency challenge <span style="font-family: var(--font-family-bold)">(“Qualifying Visit”)</span> will be specified on each participant’s individual challenge page. The required number of visits, participating brands, and minimum spend thresholds may vary by participant and brand.',
        '<p style="margin: 0.5rem 0;">Participants can earn a maximum of 1 Qualifying Visit per day per challenge upon meeting the required minimum spend displayed in the FairPrice Group app (which can be combined across multiple receipts within the same day), subject to the following channel rules:</p>',
        '<ol type="a" style="margin: 0.5rem 0 0; padding-left: 1.25rem;">',
        '<li style="margin-bottom: 0.5rem;">FairPrice and Unity Frequency challenge: Participants may use either the FairPrice Group app or present their physical Link Rewards card at checkout.</li>',
        '<li style="margin-bottom: 0.5rem;">FairPrice Online Frequency challenge: Orders must be placed using the FairPrice Group account linked to the registered Link Rewards card. Qualifying Visits from online orders will only count towards the Challenge after delivery of the order is confirmed, and the delivery date must fall within that specific monthly Challenge Period.</li>',
        '<li style="margin-bottom: 0.5rem;">Pay with Trust Frequency challenge: Payment must be made via the FairPrice Group app using a saved Trust Link or NTUC Link Credit/Debit Card at FairPrice (in-store/online), Kopitiam and Unity. Apple Pay and Google Pay transactions are not eligible.</li>',
        '</ol>',
        '</li>',
        '<li style="margin-bottom: 0.75rem;">The Organiser reserves the sole and absolute right to disqualify or reject any Participant deemed ineligible for the Challenge, be it under these Terms and Conditions or having engaged in conduct that the Organiser considers inappropriate or unacceptable.</li>',
        '<li style="margin-bottom: 0.75rem;">For enquiries or issues pertaining to the Challenge, please submit a request at <a href="https://help.fairprice.com.sg/" target="_blank" rel="noopener noreferrer" style="color: #0347A8;">https://help.fairprice.com.sg/</a></li>',
        '</ol>',
        '<h2 style="font-size: 16px; font-family: var(--font-family-bold); color: #0347A8; margin: 1.5rem 0 0.75rem;">Other Terms and Conditions:</h2>',
        '<ol start="13" style="font-size: 14px; font-family: var(--font-family-regular); line-height: 1.6; padding-left: 1.5rem; margin: 0;">',
        '<li style="margin-bottom: 0.75rem;">The Organiser reserves the sole and absolute right to cancel, suspend or amend the Challenge (in whole or in part) and/or any of these Terms and Conditions at any time without prior notice and without any liability to you.</li>',
        '<li style="margin-bottom: 0.75rem;">In the event of disputes regarding the Challenge or these Terms and Conditions, the Organiser shall have the right to make the final decision. The Organiser’s decisions in any matters relating to the Challenge shall be final, conclusive and binding.</li>',
        '<li style="margin-bottom: 0.75rem;">The Organiser shall not be liable to the Participants or any third party in any manner whatsoever for the following: (i) any cost or expense incurred by Participants for taking part in this Challenge; (ii) any consequences, including but not limited to, any claim, loss of life, injury, defect or damage, that arises out of or in connection with the Challenge; (iii) intervention by unauthorised parties during the Challenge Period; (iv) electronic or human errors in the administration of the Challenge and/or processing of entries (including any error, omission, interruption, deletion, defect, delay in operation or transmission, communications line failure, or any problems or technical malfunction of telecommunications network or lines, computer online systems, servers or providers, computer equipment, software, failure of e-mail or players on account of technical problems or traffic congestion on the internet); and (v) any lost or unsuccessful attempts to contact and notify the Participants on any matters relating to the Challenge.</li>',
        '<li style="margin-bottom: 0.75rem;">The Organiser may, at its sole and absolute discretion, substitute the Linkpoints for another item of similar value, in circumstances that include unavailability of the Linkpoints, illegality or non-feasibility.</li>',
        '<li style="margin-bottom: 0.75rem;">By participating in the Challenge, the Participants agree that the Organiser may collect, use, process and disclose his/her personal data as found in the Organiser’s record (including that furnished to the Organiser in the Link membership application), for the following purposes, and be treated in accordance with these Terms and Conditions, the Personal Data Protection Act 2012 and the Organiser’s Privacy Notice available at <a href="https://help.fairprice.com.sg/hc/en-us/articles/4464676591129-Privacy-Notice-" target="_blank" rel="noopener noreferrer" style="color: #0347A8;">https://help.fairprice.com.sg/hc/en-us/articles/4464676591129-Privacy-Notice-</a>: (a) the conduct and administration in relation to the Challenge; (b) to provide any marketing materials and communications that the Participants have agreed to receive; and (c) for any and all purposes set out in the Organiser’s Privacy Notice.</li>',
        '<li style="margin-bottom: 0.75rem;">In the event of any inconsistency between these Terms and Conditions and any brochures, marketing or promotional materials relating to this Challenge, these Terms and Conditions shall prevail.</li>',
        '<li style="margin-bottom: 0.75rem;">In the event these Terms and Conditions are translated in any language other than English language, the English language of these Terms and Conditions shall govern and take precedence over the translated version(s) to the extent of inconsistency.</li>',
        '<li style="margin-bottom: 0.75rem;">Unless otherwise provided, all Linkpoints earned from this Challenge are non-transferable and non-exchangeable for cash/credit in kind.</li>',
        '<li style="margin-bottom: 0.75rem;">These Terms and Conditions shall be governed by and construed in accordance with the laws of Singapore. All Participants shall irrevocably submit to the exclusive jurisdiction of the Singapore courts.</li>',
        '</ol>',
        '</div>',
      ],
    },
    currentChallenges: {
      meta: {
        title: 'Current Challenges | Loyalty Challenge',
        keywords:
          'loyalty, challenge, current, challenges, loyalty challenge, loyalty challenge current challenges',
        description:
          "Discover the current challenges and earn up to $30 by buying your favorite brands. Don't miss out on the opportunity to win big!",
      },
      background: 'white',
      howDoesitWork: {
        color: 'white',
        fontFamily: 'var(--font-family-bold)',
        backgroundColor: '#1454B8',
        containerBackgroundColor: 'white',
        text: 'How it works?',
        borderRadius: '6px',
        containerPadding: '2rem 0 0',
        modal: {
          background: '#FFFFFF',
          title: {
            text: 'How it works',
            color: '#333333',
            fontFamily: 'var(--font-family-bold)',
          },
          closeButton: {
            imageUrl: 'assets/images/default/close-button.svg',
            alternativeText: 'Close the tutorial',
            color: '#333333',
          },
          skipNowButton: {
            text: 'Skip for now',
            color: 'white',
            fontFamily: 'var(--font-family-bold)',
            backgroundColor: '#1454B8',
            borderRadius: '6px',
          },
        },
      },
      howToPlay: {
        specificDisplayCondition: DISPLAY_CONDITION.DEFAULT,
      },
      upButton: {
        backgroundColor: '#0347A8',
        content: '^',
      },
    },
    pastChallenges: {
      meta: {
        title: 'Loyalty Challenge | Past Challenges',
        keywords:
          'loyalty, challenge, current, challenges, loyalty challenge, loyalty challenge, past challenges',
        description:
          "Discover the current challenges and earn up to $30 by buying your favorite brands. Don't miss out on the opportunity to win big!",
      },
      banner: {
        visibility: true,
        title: 'Past Challenges',
        background: '#0347A8',
        bannerAriaLabel: '',
        styles: {
          title: {
            margin: 'unset',
            color: 'white',
            fontSize: '27px',
            fontFamily: 'var(--font-family-bold)',
          },
          banner: {
            height: '130px',
            display: 'flex',
            alignItems: 'center',
            marginBottom: '2rem',
            justifyContent: 'center',
          },
        },
      },
      accordion: {
        variant: Variants.VARIANT_1,
        iconMore: 'assets/images/scorpio/expand-more.svg',
        iconLess: 'assets/images/scorpio/expand-less.svg',
        alternativeTextExpandMore: 'Expand More',
        alternativeTextExpandLess: 'Expand Less',
        date: {
          format: 'd MMMM',
          color: '#000000',
          fontFamily: 'var(--font-family-regular)',
          template: '{firstDate} - {lastDate}',
        },
        reward: {
          color: '#004CAB',
          fontFamily: 'var(--font-family-bold)',
          label: 'Linkpoints',
        },
      },
      noChallengesText: {
        color: '#004CAB',
        fontFamily: 'var(--font-family-bold)',
        text: 'No selected challenges',
      },
      noRewardsText: {
        color: '#004CAB',
        fontFamily: 'var(--font-family-bold)',
        text: 'No rewards earned',
      },
    },
    sorry: {
      background: '#0347A8',
      content: [
        '<div style="text-align: center;"><img src="assets/images/scorpio/sorry-logo.svg" alt=""></div>',
        '<div style="font-size: 2.5rem; text-align: center; color: white; font-family: var(--font-family-bold);">Check Back Soon</div>',
        '<div style="font-size: 1rem; text-align: center; color: white; font-family: var(--font-family-regular);">Fresh challenges are loading...</div>',
      ],
    },
  },
  components: {
    header: {
      variant: Variants.VARIANT_10,
      bannerAriaLabel: '',
      backgroundColor: '#1F49FF',
      backgroundImage: {
        mobile: {
          imageUrl: 'assets/images/scorpio/cover-mobile.lottie',
        },
        tablet: {
          imageUrl: 'assets/images/scorpio/cover-mobile.lottie',
        },
        desktop: {
          imageUrl: 'assets/images/scorpio/cover-desktop.webp',
        },
      },
      campaignDate: {
        color: 'white',
        fontFamily: 'var(--font-family-regular)',
        template: '{startDate} - {endDate}',
      },
      eVoucher: {
        color: 'white',
        fontFamily: 'var(--font-family-regular)',
        text: 'Accept your challenges and<br/> shop to unlock up to <span style="color:#FCA100; font-family: var(--font-family-bold);">$30</span> in Linkpoints',
        backgroundColor: '#2149FF',
      },
    },
    brands: {
      variant: Variants.VARIANT_1,
      brandsBgColor: 'white',
      border: '2px solid #00529f',
      alternativeText: '',
    },
    footer: {
      variant: Variants.VARIANT_1,
      mobileVisiblity: true,
      showInIframe: true,
      styles: {
        display: 'flex',
        padding: '30px 0',
        alignItems: 'center',
        justifyContent: 'space-evenly',
        backgroundColor: '#0347A8',
      },
      linkStyles: {
        color: 'white',
        fontSize: '16px',
        textDecoration: 'none',
        fontFamily: 'var(--font-family-bold)',
      },
      links: [
        {
          url: 'help',
          label: 'Help Center',
        },
        {
          url: 'https://help.fairprice.com.sg/hc/en-us/articles/4464676591129-Privacy-Notice',
          external: true,
          label: 'Privacy Notice',
        },
        {
          url: 'https://support.link.sg/hc/en-us/articles/21049254552089-Terms-of-use',
          external: true,
          label: 'Term of Use',
        },
      ],
    },
    tutorial: {
      specificDisplayCondition: DISPLAY_CONDITION.ALWAYS,
      background: '#0347A8',
      carousel: {
        config: {
          sectionAriaLabel: 'Tutorial carousel',
          controlsAriaLabel: 'Carousel controls',
          paginationAriaLabel: 'Slide indicators',
          dots: {
            activeBg: 'white',
            inactiveBg: '#80B3FF',
            ariaLabel: 'Go to slide {variable} of {variable}',
          },
          slideIndicatorLabel: 'Slide {variable} on {variable}',
          controls: {
            leftIcon: {
              imageUrl: 'assets/images/scorpio/chevron-left.svg',
              alternativeText: 'Go back to the previous slide',
            },
            rightIcon: {
              alternativeText: 'Go to the next slide',
              imageUrl: 'assets/images/scorpio/chevron-right.svg',
            },
          },
        },
        items: {
          variant: Variants.VARIANT_1,
          styles: {
            title: {
              color: '#272F35',
              fontFamily: 'var(--font-family-bold)',
            },
            description: {
              color: '#6B767C',
              fontFamily: 'var(--font-family-regular)',
            },
            card: {
              border: 'none',
              background: 'white',
              borderRadius: '16px',
              boxShadow: 'none',
            },
          },
          steps: [
            {
              title: 'Step 1: Select',
              description: 'Choose up to 10 challenges to complete.',
              picture: {
                imageUrl: 'assets/images/scorpio/step-1.webp',
                alternativeText: 'Choose up to 10 challenges to complete.',
              },
            },

            {
              title: 'Step 2: Buy',
              description:
                'Shop your favourite products to start accumulating Linkpoints.',
              picture: {
                imageUrl: 'assets/images/scorpio/step-2.webp',
                alternativeText:
                  'Shop your favourite products to start accumulating Linkpoints.',
              },
            },
            {
              title: 'Step 3: Collect',
              description:
                'Collected Linkpoints will be credited to your Link account every week.',
              picture: {
                imageUrl: 'assets/images/scorpio/step-3.webp',
                alternativeText:
                  'Collected Linkpoints will be credited to your Link account every week.',
              },
            },
          ],
        },
      },
    },
    wallet: {
      variant: Variants.VARIANT_2,
      backgroundColor: 'white',
      label: {
        color: '#333333',
        fontSize: '14px',
        text: 'Linkpoints earned',
        lineHeight: '28px',
        fontFamily: 'var(--font-family-regular)',
      },
      amount: {
        color: '#FCA100',
        fontFamily: 'var(--font-family-bold)',
      },
      icon: {
        imageUrl: 'assets/images/scorpio/lp-default.svg',
        alternativeText: 'LinkPoints',
      },
    },
    navbar: {
      variant: Variants.VARIANT_3,
      menuVariant: Variants.VARIANT_2,
      hasLayoutPaddingTop: true,
      dropdownLabel: 'Menu',
      fontFamily: 'var(--font-family-regular)',
      openButton: {
        imageUrl: 'assets/images/scorpio/menu.svg',
        buttonTitle: 'Click to see more options',
        imageAlt: 'Click to see more options',
      },
      items: [
        {
          url: 'current',
          label: 'My Challenges',
          ariaLabel: 'Access to the current challenges page',
        },
        {
          url: 'history',
          label: 'Past Challenges',
          ariaLabel: 'Access to the past challenges page',
        },
        {
          url: 'help',
          label: 'How it works',
          ariaLabel: 'Access to the help page',
        },
      ],
    },
    countdown: {
      variant: Variants.VARIANT_1,
      showOnNavbarWhen: [DeviceType.DESKTOP],
      showAfterCampaignSummaryWhen: [],
      showBeforeCampaignSummaryWhen: [],
      showOnPages: 'all',
      titleAppearing: ['row', 'row', 'row'],
      titleVerticalAlignment: ['center', 'center', 'center'],
      labelStyle: {
        color: '#272F35',
        fontFamily: 'var(--font-family-regular)',
      },
      title: {
        color: '#333333',
        showTitleOnMobile: true,
        fontFamily: 'var(--font-family-regular)',
        label: 'Challenge ends in',
      },
      numberStyle: {
        color: '#333333',
        backgroundColor: '#F6F6F6',
        fontFamily: 'var(--font-family-bold)',
      },
      texts: {
        days: 'days',
        hours: 'hours',
        minutes: 'mins',
        seconds: 'secs',
      },
    },
    challenges: {
      elements: {
        winCard: {
          variant: Variants.VARIANT_7,
          backgroundColor: '#0347A8',
          completedMsg: {
            backgroundColor: 'white',
            color: '#104393',
            fontFamily: 'var(--font-family-bold)',
            text: 'Completed!',
          },
          rewardAmount: {
            fontFamily: 'var(--font-family-bold)',
            color: '#FCA100',
            iconPosition: 'after',
            picture: {
              imageUrl: 'assets/images/scorpio/lp-default.svg',
              alternativeText: 'LinkPoints',
              width: '20px',
            },
          },
          eVoucherMsg: {
            fontFamily: 'var(--font-family-regular)',
            color: 'white',
            text: 'Linkpoints will be\ncredited to you',
          },
        },
        selectionButton: {
          variant: Variants.VARIANT_5,
          button: {
            color: 'white',
            label: 'Start challenge',
            backgroundColor: '#1454B8',
            title: 'Start this challenge',
            fontFamily: 'var(--font-family-bold)',
            icon: {
              imageUrl: 'assets/images/scorpio/trophy.svg',
              alternativeText: 'Trophy icon',
            },
          },
          offerLabel: {
            color: '#333333',
            fontFamily: 'var(--font-family-regular)',
            textByType: {
              [OfferType.STEP]: 'Collect up to',
              [OfferType.FREQUENCY]: 'Collect',
            },
          },
          rewardContainer: {
            textColor: '#FFFFFF',
            backgroundColor: '#FCA100',
            fontFamily: 'var(--font-family-bold)',
          },
          suffixLabel: {
            color: '#333333',
            text: 'Linkpoints',
            fontFamily: 'var(--font-family-regular)',
          },
          frequencyVisitsLabel: {
            fontFamily: 'var(--font-family-bold)',
            textColor: '#333333',
            textBySubType: {
              [SubType.DEFAULT]: 'Shop {variable} times at FairPrice stores',
              [ScorpioSubType.INSTORE]:
                'Shop {variable} times at FairPrice stores',
              [ScorpioSubType.ONLINE]:
                'Complete {variable} online delivery orders',
              [ScorpioSubType.UNITY]:
                'Shop {variable} times at Unity standalone stores',
              [ScorpioSubType.PAYWITHTRUST]:
                'Shop {variable} times and pay with Trust',
            },
          },
        },
        unselectionButton: {
          variant: Variants.VARIANT_2,
          text: 'Deactivate',
          title: 'Deactivate this challenge',
          color: '#E00000',
          fontFamily: 'var(--font-family-bold)',
        },
        outOfStock: {
          variant: Variants.VARIANT_1,
          backgroundColor: 'rgba(3, 71, 168, 0.78)',
          borderRadius: '16px',
          banner: {
            textColor: '#0347A8',
            borderRadius: '8px',
            backgroundColor: 'white',
            text: 'Challenge fully redeemed',
            fontFamily: 'var(--font-family-bold)',
          },
        },
      },
      step: {
        [SubType.DEFAULT]: {
          variant: Variants.VARIANT_2,
          bannerAlternativeText: 'Challenges banner',
          logoAlternativeText: 'Logo banner',
          cardStyles: {
            border: 'none',
            boxShadow: '#b1b1b1 0px 4px 24px',
          },
          header: {
            height: '143px',
            backgroundColor: '#0347A8',
          },
          subHeader: {
            color: '#333333',
            useDescription: false,
            backgroundColor: 'white',
            completedBackgroundColor: '#0347A8',
            fontFamily: 'var(--font-family-bold)',
            fontSize: '12px',
          },
          body: {
            progressbar: {
              containerColor: '#EAEAEA',
              progressColor: '#3FC078',
              spendLabel: 'Spend',
              labelColor: '#1454B8',
              spendLabelTop: '20px',
            },
            rewards: {
              color: '#004CAB',
              fontFamily: 'var(--font-family-bold)',
              defaultPath: 'assets/images/scorpio/reward-rec.svg',
              bubbleHeight: '48px',
              icons: {
                reached: {
                  imageUrl: 'assets/images/scorpio/lp-reached.svg',
                  alternativeText: 'LinkPoints',
                },
                default: {
                  imageUrl: 'assets/images/scorpio/lp-reached.svg',
                  alternativeText: 'LinkPoints',
                },
                unreached: {
                  imageUrl: 'assets/images/scorpio/lp-default.svg',
                  alternativeText: 'LinkPoints',
                },
              },
              stepCompleted: {
                imageUrl: 'assets/images/scorpio/step-completed.svg',
                alternativeText: 'Step completed',
              },
              alternativeText: {
                reached: 'I won ${variable}',
                unreached: 'I win ${variable}',
                default: 'I won ${variable}',
              },
              backgroundColors: {
                reached: { fill: '#FCA100', stroke: '#FCA100' },
                unreached: { fill: 'white', stroke: '#0710AC' },
                default: { fill: '#FEE3B3', stroke: '#FEE3B3' },
              },
            },
            targets: {
              height: '30px',
              color: '#004CAB',
              borderRadius: '50%',
              backgroundColor: 'white',
              fontFamily: 'var(--font-family-bold)',
              targetUnReachedTitle: '',
              onIcon: {
                imageUrl: 'assets/images/scorpio/picture-reached.svg',
                onIconAlternativeText: 'I win ${variable}',
              },
              offIcon: {
                imageUrl: 'assets/images/scorpio/picture-reached.svg',
                offIconAlternativeText: 'I won ${variable}',
              },
              border: '1px solid var(--challenge-primary-color)',
            },
          },
          footer: {
            color: '#1454B8',
            label: 'Total spent',
            fontFamily: 'var(--font-family-regular)',
            spentAmount: {
              color: '#1454B8',
              borderRadius: '',
              backgroundColor: '',
              fontFamily: 'var(--font-family-bold)',
            },
            productCheckButton: {
              ariaLabel: 'Click to check eligible products.',
              color: '#1454B8',
              backgroundColor: 'white',
              label: 'Eligible products',
              textDecoration: 'underline',
              fontFamily: 'var(--font-family-bold)',
            },
          },
        },
        // Same card as the default scope, except the scope information is
        // reached through the subheader's info button instead of the footer's
        // "eligible products" link — hence no productCheckButton (EAI-2302).
        [ScorpioSubType.KOPITIAM]: {
          variant: Variants.VARIANT_2,
          bannerAlternativeText: 'Challenges banner',
          logoAlternativeText: 'Logo banner',
          cardStyles: {
            border: 'none',
            boxShadow: '#b1b1b1 0px 4px 24px',
          },
          header: {
            height: '143px',
            backgroundColor: '#0347A8',
          },
          subHeader: {
            color: '#333333',
            useDescription: false,
            scopeInfoButton: {
              imageUrl: 'assets/images/scorpio/step-info-button.svg',
              alternativeText: 'Open challenge information',
            },
            backgroundColor: 'white',
            completedBackgroundColor: '#0347A8',
            fontFamily: 'var(--font-family-bold)',
            fontSize: '12px',
          },
          body: {
            progressbar: {
              containerColor: '#EAEAEA',
              progressColor: '#3FC078',
              spendLabel: 'Spend',
              labelColor: '#1454B8',
              spendLabelTop: '20px',
            },
            rewards: {
              color: '#004CAB',
              fontFamily: 'var(--font-family-bold)',
              defaultPath: 'assets/images/scorpio/reward-rec.svg',
              bubbleHeight: '48px',
              icons: {
                reached: {
                  imageUrl: 'assets/images/scorpio/lp-reached.svg',
                  alternativeText: 'LinkPoints',
                },
                default: {
                  imageUrl: 'assets/images/scorpio/lp-reached.svg',
                  alternativeText: 'LinkPoints',
                },
                unreached: {
                  imageUrl: 'assets/images/scorpio/lp-default.svg',
                  alternativeText: 'LinkPoints',
                },
              },
              stepCompleted: {
                imageUrl: 'assets/images/scorpio/step-completed.svg',
                alternativeText: 'Step completed',
              },
              alternativeText: {
                reached: 'I won ${variable}',
                unreached: 'I win ${variable}',
                default: 'I won ${variable}',
              },
              backgroundColors: {
                reached: { fill: '#FCA100', stroke: '#FCA100' },
                unreached: { fill: 'white', stroke: '#0710AC' },
                default: { fill: '#FEE3B3', stroke: '#FEE3B3' },
              },
            },
            targets: {
              height: '30px',
              color: '#004CAB',
              borderRadius: '50%',
              backgroundColor: 'white',
              fontFamily: 'var(--font-family-bold)',
              targetUnReachedTitle: '',
              onIcon: {
                imageUrl: 'assets/images/scorpio/picture-reached.svg',
                onIconAlternativeText: 'I win ${variable}',
              },
              offIcon: {
                imageUrl: 'assets/images/scorpio/picture-reached.svg',
                offIconAlternativeText: 'I won ${variable}',
              },
              border: '1px solid var(--challenge-primary-color)',
            },
          },
          footer: {
            color: '#1454B8',
            label: 'Total spent',
            fontFamily: 'var(--font-family-regular)',
            spentAmount: {
              color: '#1454B8',
              borderRadius: '',
              backgroundColor: '',
              fontFamily: 'var(--font-family-bold)',
            },
          },
        },
      },
      frequency: {
        [SubType.DEFAULT]: {
          variant: Variants.VARIANT_1,
          boxShadow: '#b1b1b1 0px 4px 24px',
          header: {
            backgroundImage: {
              url: 'assets/images/scorpio/frequency-in-store.webp',
              bannerAriaLabel: 'Frequency banner',
            },
            height: '143px',
          },

          headerRewardRatio: {
            backgroundColor: '#FCA100',
            color: 'white',
            fontFamily: 'var(--font-family-bold)',
            collectUpTo: {
              text: 'Collect',
            },
            points: {
              text: 'Linkpoints',
            },
          },
          subHeader: {
            borderBottom: 'none',
            backgroundColor: 'white',
            completedBackgroundColor: '#0347A8',
            title: {
              color: '#333333',
              label: '1 visit/day: Min. ${variable} spend (combined receipts)',
              fontFamily: 'var(--font-family-bold)',
            },
          },
          body: {
            label: 'Shop {variable} times at FairPrice stores',
            labelFontFamily: 'var(--font-family-bold)',
            labelFontSize: '14px',
            stamps: {
              showNumbers: true,
              iconSelected: 'assets/images/scorpio/frequency-stamp-success.svg',
              iconNonSelected:
                'assets/images/scorpio/frequency-stamp-empty.svg',
            },
          },
          footer: {
            color: '#1454B8',
            label: 'Visits: ',
            fontFamily: 'var(--font-family-bold)',
            remainingVisitsLabel: {
              color: '#1454B8',
              text: 'Only {variable} more {visit|visits} to go.',
              fontFamily: 'var(--font-family-bold)',
            },
          },
        },
        [ScorpioSubType.INSTORE]: {
          variant: Variants.VARIANT_1,
          boxShadow: '#b1b1b1 0px 4px 24px',
          header: {
            backgroundImage: {
              url: 'assets/images/scorpio/frequency-in-store.webp',
              bannerAriaLabel: 'Frequency banner',
            },
            height: '143px',
          },

          headerRewardRatio: {
            backgroundColor: '#FCA100',
            color: 'white',
            fontFamily: 'var(--font-family-bold)',
            collectUpTo: {
              text: 'Collect',
            },
            points: {
              text: 'Linkpoints',
            },
          },
          subHeader: {
            borderBottom: 'none',
            backgroundColor: 'white',
            completedBackgroundColor: '#0347A8',
            title: {
              color: '#333333',
              label: '1 visit/day: Min. ${variable} spend (combined receipts)',
              fontFamily: 'var(--font-family-bold)',
            },
          },
          body: {
            label: 'Shop {variable} times at FairPrice stores',
            labelFontFamily: 'var(--font-family-bold)',
            labelFontSize: '14px',
            stamps: {
              showNumbers: true,
              iconSelected: 'assets/images/scorpio/frequency-stamp-success.svg',
              iconNonSelected:
                'assets/images/scorpio/frequency-stamp-empty.svg',
            },
          },
          footer: {
            color: '#1454B8',
            label: 'Visits: ',
            fontFamily: 'var(--font-family-bold)',
            remainingVisitsLabel: {
              color: '#1454B8',
              text: 'Only {variable} more {visit|visits} to go.',
              fontFamily: 'var(--font-family-bold)',
            },
          },
        },
        [ScorpioSubType.ONLINE]: {
          variant: Variants.VARIANT_1,
          boxShadow: '#b1b1b1 0px 4px 24px',
          header: {
            backgroundImage: {
              url: 'assets/images/scorpio/frequency-online.webp',
              bannerAriaLabel: 'Frequency banner',
            },
            height: '143px',
          },
          headerRewardRatio: {
            backgroundColor: '#FCA100',
            color: 'white',
            fontFamily: 'var(--font-family-bold)',
            collectUpTo: {
              text: 'Collect',
            },
            points: {
              text: 'Linkpoints',
            },
          },
          subHeader: {
            borderBottom: 'none',
            backgroundColor: 'white',
            completedBackgroundColor: '#0347A8',
            title: {
              color: '#333333',
              label: '1 visit/day: Min. ${variable} spend (combined receipts)',
              fontFamily: 'var(--font-family-bold)',
            },
          },
          body: {
            label: 'Complete {variable} online delivery orders',
            labelFontFamily: 'var(--font-family-bold)',
            labelFontSize: '14px',
            stamps: {
              showNumbers: true,
              iconSelected: 'assets/images/scorpio/frequency-stamp-success.svg',
              iconNonSelected:
                'assets/images/scorpio/frequency-stamp-empty.svg',
            },
          },
          footer: {
            color: '#1454B8',
            label: 'Visits: ',
            fontFamily: 'var(--font-family-bold)',
            remainingVisitsLabel: {
              color: '#1454B8',
              text: 'Only {variable} more {visit|visits} to go.',
              fontFamily: 'var(--font-family-bold)',
            },
          },
        },
        [ScorpioSubType.UNITY]: {
          variant: Variants.VARIANT_1,
          boxShadow: '#b1b1b1 0px 4px 24px',
          header: {
            backgroundImage: {
              url: 'assets/images/scorpio/frequency-unity.webp',
              bannerAriaLabel: 'Frequency banner',
            },
            height: '143px',
          },

          headerRewardRatio: {
            backgroundColor: '#FCA100',
            color: 'white',
            fontFamily: 'var(--font-family-bold)',
            collectUpTo: {
              text: 'Collect',
            },
            points: {
              text: 'Linkpoints',
            },
          },
          subHeader: {
            borderBottom: 'none',
            backgroundColor: 'white',
            completedBackgroundColor: '#0347A8',
            title: {
              color: '#333333',
              label: '1 visit/day: Min. ${variable} spend (combined receipts)',
              fontFamily: 'var(--font-family-bold)',
            },
            picture: {
              imageUrl: 'assets/images/scorpio/frequency-info-button.svg',
              alternativeText: 'Open frequency challenge information',
            },
          },
          body: {
            label: 'Shop {variable} times at Unity standalone stores',
            labelFontFamily: 'var(--font-family-bold)',
            labelFontSize: '14px',
            stamps: {
              showNumbers: true,
              iconSelected: 'assets/images/scorpio/frequency-stamp-success.svg',
              iconNonSelected:
                'assets/images/scorpio/frequency-stamp-empty.svg',
            },
          },
          footer: {
            color: '#1454B8',
            label: 'Visits: ',
            fontFamily: 'var(--font-family-bold)',
            remainingVisitsLabel: {
              color: '#1454B8',
              text: 'Only {variable} more {visit|visits} to go.',
              fontFamily: 'var(--font-family-bold)',
            },
          },
        },
        [ScorpioSubType.PAYWITHTRUST]: {
          variant: Variants.VARIANT_1,
          boxShadow: '#b1b1b1 0px 4px 24px',
          header: {
            backgroundImage: {
              url: 'assets/images/scorpio/frequency-pay-with-trust.webp',
              bannerAriaLabel: 'Frequency banner',
            },
            height: '143px',
          },
          headerRewardRatio: {
            backgroundColor: '#FCA100',
            color: 'white',
            fontFamily: 'var(--font-family-bold)',
            collectUpTo: {
              text: 'Collect',
            },
            points: {
              text: 'Linkpoints',
            },
          },
          subHeader: {
            borderBottom: 'none',
            backgroundColor: 'white',
            completedBackgroundColor: '#0347A8',
            title: {
              color: '#333333',
              label: '1 visit/day: Min. ${variable} spend (combined receipts)',
              fontFamily: 'var(--font-family-bold)',
            },
            picture: {
              imageUrl: 'assets/images/scorpio/frequency-info-button.svg',
              alternativeText: 'Open frequency challenge information',
            },
          },
          body: {
            label: 'Shop {variable} times and pay with Trust',
            labelFontFamily: 'var(--font-family-bold)',
            labelFontSize: '14px',
            stamps: {
              showNumbers: true,
              iconSelected: 'assets/images/scorpio/frequency-stamp-success.svg',
              iconNonSelected:
                'assets/images/scorpio/frequency-stamp-empty.svg',
            },
          },
          footer: {
            color: '#1454B8',
            label: 'Visits: ',
            fontFamily: 'var(--font-family-bold)',
            remainingVisitsLabel: {
              color: '#1454B8',
              text: 'Only {variable} more {visit|visits} to go.',
              fontFamily: 'var(--font-family-bold)',
            },
          },
        },
      },
    },
    challengeSummary: {
      variant: Variants.VARIANT_1,
      stackOnMobile: false,
      title: {
        color: 'black',
        text: 'Your challenges',
        fontFamily: 'var(--font-family-bold)',
        transform: 'none',
      },
      challengeLabels: {
        onGoingLabel: {
          color: '#333333',
          text: 'Ongoing',
          fontFamily: 'var(--font-family-regular)',
        },
        completedLabel: {
          color: '#333333',
          text: 'Completed',
          fontFamily: 'var(--font-family-regular)',
        },
      },
      counterBorderRadius: '5px',
      challengesInfos: {
        color: 'white',
        backgroundColor: '#1454B8',
        fontFamily: 'var(--font-family-bold)',
      },
      update: {
        color: '#333333',
        text: 'Last transaction: {variable}',
        defaultFontSize: '16px',
        fontFamily: 'var(--font-family-regular)',
        dateFormat: 'dd/MM/yyyy',
      },
      pointsConversionMsg: {
        color: 'white',
        backgroundColor: '#1454B8',
        text: '100 Linkpoints = $1',
        fontFamily: 'var(--font-family-bold)',
      },
    },
    modals: {
      closeButton: {
        label: 'Close',
        textColor: 'white',
        borderRadius: '50px',
        backgroundColor: 'var(--challenge-primary-color)',
        fontFamily: 'var(--font-family-bold)',
        ariaLabel: 'Close this dialog',
      },
      unselectChallenge: {
        backgroundColor: '#FFFFFF',
        header: {
          backgroundColor: '',
          backgroundImage: '',
        },
        frequencyHeaderImages: {
          [SubType.DEFAULT]: {
            imageUrl: 'assets/images/scorpio/frequency-in-store.webp',
            alternativeText: 'Frequency challenge',
          },
          [ScorpioSubType.INSTORE]: {
            imageUrl: 'assets/images/scorpio/frequency-in-store.webp',
            alternativeText: 'FairPrice frequency challenge',
          },
          [ScorpioSubType.ONLINE]: {
            imageUrl: 'assets/images/scorpio/frequency-online.webp',
            alternativeText: 'Online frequency challenge',
          },
          [ScorpioSubType.UNITY]: {
            imageUrl: 'assets/images/scorpio/frequency-unity.webp',
            alternativeText: 'Unity frequency challenge',
          },
          [ScorpioSubType.PAYWITHTRUST]: {
            imageUrl: 'assets/images/scorpio/frequency-pay-with-trust.webp',
            alternativeText: 'Pay with trust frequency challenge',
          },
        },
        title: {
          color: '#272F35',
          fontFamily: 'var(--font-family-bold)',
          label: 'Cancel this challenge?',
        },
        body: {
          color: '#333333',
          fontFamily: 'var(--font-family-regular)',
          label: 'You may select this challenge again if you change your mind',
        },

        actionButtons: {
          variant: Variants.VARIANT_2,
          confirmButton: {
            borderRadius: '8px',
            textColor: '#FFFFFF',
            backgroundColor: '#C10B3A',
            label: 'Yes, cancel challenge',
            fontFamily: 'var(--font-family-bold)',
            ariaLabel: 'Yes, cancel this challenge',
          },
          closeButton: {
            label: 'Back',
            borderRadius: '',
            textColor: '#1454B8',
            backgroundColor: 'white',
            fontFamily: 'var(--font-family-regular)',
            ariaLabel: 'Back to challenges',
          },
        },
      },
      scopeInfo: {
        ariaLabel: 'Challenge information',
        backgroundColor: '#FFFFFF',
        title: {
          color: '#333333',
          fontFamily: 'var(--font-family-bold)',
        },
        body: {
          color: '#272F35',
          fontFamily: 'var(--font-family-regular)',
        },
      },
      visitInfo: {
        backgroundColor: '#FFFFFF',
        bodyBySubType: {
          [SubType.UNITY]: {
            color: '#272F35',
            label: 'Unity standalone stores exclude Unity in FairPrice stores.',
            fontFamily: 'var(--font-family-bold)',
          },
          [SubType.PAYWITHTRUST]: {
            color: '#272F35',
            label:
              'Payment must be made using a Trust Link/NTUC Link card linked to the FairPrice Group app at FairPrice (in-store/online), Kopitiam and Unity. Apple Pay and Google Pay transactions are not eligible.',
            fontFamily: 'var(--font-family-bold)',
          },
        },
      },
      productCheck: {
        ariaLabel: '',
        backgroundColor: '#FFFFFF',
        fontFamily: 'var(--font-family)',
        tabs: [
          {
            type: ProductCheckTabType.SCAN,
            checkMode: ScanCheckMode.PRODUCT_LIST,
            label: 'Scan',
            title: 'Barcode Verification',
            readers: [BARCODE_FORMAT.UPC_A],
            successMessage: 'Success',
            errorMessage: 'NOT ELIGIBLE FOR THIS CHALLENGE',
            startButtonLabel: 'Start scanner',
            retryPermissionButtonLabel: 'Retry camera access',
            failureMessage: 'AN ERROR OCCURRED, PLEASE TRY AGAIN',
            restartButtonLabel: 'Restart Scan',
            errorWhenCameraPermissionDenied:
              'Please allow camera access to use the scanner.',
            errorWhenCameraNotFound: 'Camera not available',
            errorWhenCameraBusy:
              'The camera is in use by another app. Close it, then try again.',
            previewLabel: 'Barcode scanner preview',
            subtitle: 'Scan your product here',
          },
          {
            type: ProductCheckTabType.PRODUCT_LIST,
            failureMessage: 'AN ERROR OCCURRED, PLEASE TRY AGAIN',
            label: 'Enter manually',
            searchable: true,
            inputPlaceholder: 'Enter a barcode to search',
            tableTitle: {
              text: 'List of eligible products',
              color: 'var(--challenge-primary-color)',
            },
            columns: {
              name: {
                header: 'Product',
                color: '#000000',
                fontFamily: 'var(--font-family-regular)',
              },
              code: {
                header: 'Barcode',
                color: '#000000',
                fontFamily: 'var(--font-family-regular)',
              },
            },
          },
        ],
        tabConfig: {
          activeColor: 'white',
          inactiveColor: 'black',
          inactiveBackground: 'white',
          fontFamily: 'var(--font-family)',
          inactiveBorderBottom: '2px solid #e6e6e6',
          activeBackground: 'var(--challenge-primary-color)',
          activeBorderBottom: '2px solid var(--challenge-primary-color)',
        },
      },
      firstSelection: {
        backgroundColor: 'white',
        title: {
          color: '#272F35',
          label: 'Yay! You started</br> your first challenge',
          fontFamily: 'var(--font-family-bold)',
        },
        header: {
          backgroundImage: '',
          backgroundColor: 'white',
          logo: {
            imageUrl: 'assets/images/scorpio/first-selection.svg',
            alternativeText: 'You have selected the first challenge',
          },
        },
        body: {
          color: '#333333',
          fontFamily: 'var(--font-family-regular)',
          label: 'You can select {variable} more challenges.',
        },
      },
      lastSelection: {
        backgroundColor: 'white',
        title: {
          color: '#272F35',
          fontFamily: 'var(--font-family-bold)',
          label: '{variable} challenges selected!',
        },
        header: {
          backgroundImage: '',
          backgroundColor: 'white',
          logo: {
            imageUrl: 'assets/images/scorpio/last-selection.svg',
            alternativeText:
              '10 selected! Great—activate 10 favorites and finish by shopping with us.',
          },
        },
        body: {
          color: '#6B767C',
          fontFamily: 'var(--font-family-regular)',
          label:
            'Start shopping to complete the challenges and earn Linkpoints!',
        },
      },
      maximumSelection: {
        backgroundColor: 'white',
        title: {
          color: '#272F35',
          fontFamily: 'var(--font-family-bold)',
          label: 'You have hit maximum number of challenges',
        },
        header: {
          backgroundImage: '',
          backgroundColor: 'white',
          logo: {
            imageUrl: 'assets/images/scorpio/maximum-selection.svg',
            alternativeText: 'Max challenges reached. Cancel one to start.',
          },
        },
        body: {
          color: '#333333',
          fontFamily: 'var(--font-family-regular)',
          label: 'Cancel one of your challenges to start this one.',
        },
      },
      outOfStockIssue: {
        backgroundColor: '#FFFFFF',
        title: {
          color: '#272F35',
          fontFamily: 'var(--font-family-bold)',
          label: 'Limit reached',
        },
        body: {
          color: '#333333',
          fontFamily: 'var(--font-family-regular)',
          label:
            'This challenge has reached the maximum number of participants. Please choose another.',
        },
      },
      genericError: {
        backgroundColor: '#FFFFFF',
        title: {
          color: '#272F35',
          fontFamily: 'var(--font-family-bold)',
          label: 'Error',
        },
        body: {
          color: '#333333',
          fontFamily: 'var(--font-family-regular)',
          label:
            "An error occurred, and the challenge couldn't be selected. Please check your connection and try again.",
        },
      },
      timeoutError: {
        backgroundColor: '#FFFFFF',
        title: {
          color: '#272F35',
          fontFamily: 'var(--font-family-bold)',
          label: 'Request timed out',
        },
        body: {
          color: '#333333',
          fontFamily: 'var(--font-family-regular)',
          label:
            'Selecting the challenge took too long. Please check your connection and try again.',
        },
      },
      challengeUnavailable: {
        backgroundColor: '#FFFFFF',
        title: {
          color: '#272F35',
          fontFamily: 'var(--font-family-bold)',
          label: 'Campaign ended',
        },
        body: {
          color: '#333333',
          fontFamily: 'var(--font-family-regular)',
          label:
            'This campaign has ended and challenges are no longer available for selection.',
        },
      },
    },
  },
};
